import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { useToast } from '../../hooks/use-toast';
import { Plus, Edit, Trash2, CheckCircle, XCircle, Building2, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const BrandManagement = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    website: '',
    industry_category: '',
    description: ''
  });
  
  const [approvalData, setApprovalData] = useState({
    approval_status: 'approved',
    approval_notes: '',
    contract_start_date: '',
    contract_end_date: ''
  });

  useEffect(() => {
    fetchBrands();
  }, [filterStatus]);

  const fetchBrands = async () => {
    try {
      setLoading(true);
      const params = filterStatus !== 'all' ? `?approval_status=${filterStatus}` : '';
      const response = await axios.get(`${backendUrl}/api/admin/brands${params}`);
      setBrands(response.data || []);
    } catch (error) {
      console.error('Error fetching brands:', error);
      toast({
        title: 'Error',
        description: 'Failed to load brands',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (selectedBrand) {
        await axios.put(`${backendUrl}/api/admin/brands/${selectedBrand.id}`, formData);
        toast({ title: 'Success', description: 'Brand updated successfully' });
      } else {
        await axios.post(`${backendUrl}/api/admin/brands`, formData);
        toast({ title: 'Success', description: 'Brand created successfully' });
      }
      
      setDialogOpen(false);
      resetForm();
      fetchBrands();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to save brand',
        variant: 'destructive'
      });
    }
  };

  const handleApproval = async () => {
    try {
      await axios.put(
        `${backendUrl}/api/admin/brands/${selectedBrand.id}/approve`,
        approvalData
      );
      
      toast({
        title: 'Success',
        description: `Brand ${approvalData.approval_status} successfully`
      });
      
      setApprovalDialogOpen(false);
      setSelectedBrand(null);
      setApprovalData({
        approval_status: 'approved',
        approval_notes: '',
        contract_start_date: '',
        contract_end_date: ''
      });
      fetchBrands();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to process approval',
        variant: 'destructive'
      });
    }
  };

  const handleDelete = async (brandId) => {
    if (!window.confirm('Are you sure you want to archive this brand? It can be restored later.')) return;
    
    try {
      await axios.delete(`${backendUrl}/api/admin/brands/${brandId}`);
      toast({ title: 'Success', description: 'Brand archived successfully' });
      fetchBrands();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to delete brand',
        variant: 'destructive'
      });
    }
  };

  const openEditDialog = (brand) => {
    setSelectedBrand(brand);
    setFormData({
      company_name: brand.company_name,
      contact_name: brand.contact_name,
      email: brand.email,
      phone: brand.phone,
      website: brand.website || '',
      industry_category: brand.industry_category,
      description: brand.description || ''
    });
    setDialogOpen(true);
  };

  const openApprovalDialog = (brand) => {
    setSelectedBrand(brand);
    setApprovalDialogOpen(true);
  };

  const resetForm = () => {
    setSelectedBrand(null);
    setFormData({
      company_name: '',
      contact_name: '',
      email: '',
      phone: '',
      website: '',
      industry_category: '',
      description: ''
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <button
          onClick={() => navigate('/admin')}
          className="mb-4 flex items-center gap-2 text-sm text-gray-600 hover:text-[#2c3e7d] transition-colors"
        >
          <ArrowLeft size={16} /> Back to Admin Dashboard
        </button>
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Brand Management</h1>
          <p className="text-gray-600 mt-1">Manage advertising brand partners</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm}>
              <Plus size={16} className="mr-2" />
              Add Brand
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedBrand ? 'Edit Brand' : 'Add New Brand'}</DialogTitle>
              <DialogDescription>
                {selectedBrand ? 'Update brand partner information' : 'Add a new brand partner'}
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="company_name">Company Name *</Label>
                  <Input
                    id="company_name"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="contact_name">Contact Name *</Label>
                  <Input
                    id="contact_name"
                    name="contact_name"
                    value={formData.contact_name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="phone">Phone *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="website">Website</Label>
                  <Input
                    id="website"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    placeholder="https://example.com"
                  />
                </div>
                
                <div>
                  <Label htmlFor="industry_category">Industry Category *</Label>
                  <Input
                    id="industry_category"
                    name="industry_category"
                    value={formData.industry_category}
                    onChange={handleInputChange}
                    placeholder="e.g., Sports Equipment"
                    required
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="Brief description of the brand"
                />
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  {selectedBrand ? 'Update' : 'Create'} Brand
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex gap-4 items-center">
            <Label>Filter by Status:</Label>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Brands</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Brands List */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#2c3e7d]"></div>
        </div>
      ) : brands.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Building2 className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p className="text-gray-600">No brands found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {brands.map((brand) => (
            <Card key={brand.id}>
              <CardContent className="p-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-bold text-gray-900">{brand.company_name}</h3>
                      {getStatusBadge(brand.approval_status)}
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
                      <div>
                        <p className="text-gray-600">Contact: {brand.contact_name}</p>
                        <p className="text-gray-600">Email: {brand.email}</p>
                        <p className="text-gray-600">Phone: {brand.phone}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Category: {brand.industry_category}</p>
                        {brand.website && (
                          <p className="text-gray-600">Website: {brand.website}</p>
                        )}
                        {brand.contract_start_date && (
                          <p className="text-gray-600">
                            Contract: {brand.contract_start_date} to {brand.contract_end_date}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {brand.description && (
                      <p className="text-gray-600 mt-3">{brand.description}</p>
                    )}
                    
                    {brand.approval_notes && (
                      <div className="mt-3 p-3 bg-gray-50 rounded">
                        <p className="text-sm text-gray-600">
                          <strong>Notes:</strong> {brand.approval_notes}
                        </p>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex gap-2 ml-4">
                    {brand.approval_status === 'pending' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openApprovalDialog(brand)}
                      >
                        <CheckCircle size={16} className="mr-1" />
                        Review
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEditDialog(brand)}
                    >
                      <Edit size={16} />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(brand.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Approval Dialog */}
      <Dialog open={approvalDialogOpen} onOpenChange={setApprovalDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Review Brand: {selectedBrand?.company_name}</DialogTitle>
            <DialogDescription>
              Approve or reject this brand partner
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label>Decision</Label>
              <Select
                value={approvalData.approval_status}
                onValueChange={(value) => setApprovalData({ ...approvalData, approval_status: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="approved">Approve</SelectItem>
                  <SelectItem value="rejected">Reject</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {approvalData.approval_status === 'approved' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Contract Start Date</Label>
                  <Input
                    type="date"
                    value={approvalData.contract_start_date}
                    onChange={(e) => setApprovalData({ ...approvalData, contract_start_date: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Contract End Date</Label>
                  <Input
                    type="date"
                    value={approvalData.contract_end_date}
                    onChange={(e) => setApprovalData({ ...approvalData, contract_end_date: e.target.value })}
                  />
                </div>
              </div>
            )}
            
            <div>
              <Label>Notes</Label>
              <Textarea
                value={approvalData.approval_notes}
                onChange={(e) => setApprovalData({ ...approvalData, approval_notes: e.target.value })}
                rows={3}
                placeholder="Add any notes about this decision"
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setApprovalDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleApproval}>
                Submit Decision
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
};

export default BrandManagement;
